import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';

import { restoreAttractionOffer } from '~/services/AttractionsService';

import { setCacheBuster } from './attractionCacheBuster';
import { attractionQueryKeys } from './attractionQueryKeys';
import { useCanonicalAttractionRedirect } from './useCanonicalAttractionRedirect';

interface RestoreAttractionOfferData {
  attractionId: string;
  offerId: string;
}

export function useRestoreAttractionOffer() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  const { retargetFromMutationError } = useCanonicalAttractionRedirect();

  return useMutation({
    mutationFn: async ({ attractionId, offerId }: RestoreAttractionOfferData) => {
      return await restoreAttractionOffer(attractionId, offerId);
    },
    onSuccess: (_data, { attractionId }) => {
      queryClient.invalidateQueries({ queryKey: attractionQueryKeys.offers(attractionId) });
      queryClient.invalidateQueries({ queryKey: attractionQueryKeys.lists() });
      queryClient.invalidateQueries({ queryKey: attractionQueryKeys.detail(attractionId) });
      setCacheBuster(attractionId, Date.now().toString());
      enqueueSnackbar('Offer restored to attraction', { variant: 'success' });
    },
    onError: (error: Error, variables) => {
      if (retargetFromMutationError(error, variables.attractionId)) return;
      enqueueSnackbar(`Error restoring offer: ${error.message || 'Unknown error'}`, { variant: 'error' });
    },
  });
}
